import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { isNil } from 'lodash-es';

import type { LocationHistoryMinimal } from '~hooks/useLocationHistory/models';
import { useStores } from '~store';
import { formatDuration } from '~utils/dateTime';
import { formatSpeed as formatSpeedUtil } from '~utils/speed';

import { MAX_VALID_SPEED_MS, MIN_VALID_SPEED_MS } from './constants';

interface TimelineInfoPanelProps {
  ping: LocationHistoryMinimal | null;
  loadNumber?: number | null;
  cycleSeconds?: number | null;
}

export const TimelineInfoPanel = ({
  ping,
  loadNumber,
  cycleSeconds,
}: TimelineInfoPanelProps) => {
  const { userStore } = useStores();

  const isMetric = userStore?.userCompany.isMeters;

  const formatSpeed = (speedStr: string | null) => {
    if (!speedStr) {
      return t('common.not_applicable');
    }

    const speed = parseFloat(speedStr);

    if (speed < MIN_VALID_SPEED_MS || speed > MAX_VALID_SPEED_MS) {
      return t('common.not_applicable');
    }

    return formatSpeedUtil(speed, !!isMetric);
  };

  const formatTime = (ping: LocationHistoryMinimal | null) => {
    if (!ping) {
      return t('common.not_applicable');
    }

    return dayjs.tz(ping.occurredAt.valueOf()).format('h:mm A');
  };

  const valueSx = { fontVariantNumeric: 'tabular-nums' as const };
  const labelSx = {
    color: 'text.secondary',
    lineHeight: 1.2,
    whiteSpace: 'nowrap' as const,
  };
  const fieldSx = {
    display: 'flex',
    flexDirection: 'column' as const,
    minWidth: 0,
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        flexWrap: 'wrap',
        rowGap: 0.5,
        columnGap: 2.5,
      }}
    >
      {!isNil(loadNumber) && (
        <Box sx={fieldSx}>
          <Typography variant="caption" sx={labelSx}>
            {t('live_map.timeline.load')}
          </Typography>
          <Typography variant="body2" sx={valueSx}>
            {loadNumber}
          </Typography>
        </Box>
      )}
      <Box sx={fieldSx}>
        <Typography variant="caption" sx={labelSx}>
          {t('live_map.timeline.time')}
        </Typography>
        <Typography variant="body2" sx={valueSx}>
          {formatTime(ping)}
        </Typography>
      </Box>
      <Box sx={fieldSx}>
        <Typography variant="caption" sx={labelSx}>
          {t('live_map.timeline.speed')}
        </Typography>
        <Typography variant="body2" sx={valueSx}>
          {formatSpeed(ping?.speed || null)}
        </Typography>
      </Box>
      {!isNil(cycleSeconds) && (
        <Box sx={fieldSx}>
          <Typography variant="caption" sx={labelSx}>
            {t('live_map.timeline.cycle')}
          </Typography>
          <Typography variant="body2" sx={valueSx}>
            {formatDuration(cycleSeconds)}
          </Typography>
        </Box>
      )}
    </Box>
  );
};
